import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { CustomHttpResponse } from '../../classes/http';
import {
  IBaseStandard,
  ICountryStandard,
  ICountryStandardsResponse,
  IFederalStandard,
  IFederalStandardsResponse,
  IMarketBranchResponse,
  IRegionStandard,
  IRegionStandardsResponse,
  ISectorStandard,
  ISectorStandardsResponse,
  IStandardsResponse,
  IUpdateStandardPayload,
  INormativeRow,
  INormativeCell,
  INormativeView,
  IUpdateResponse,
  ISectorSpeedStandard,
  ISectorSpeedStandardsParams,
  IUpdateSectorSpeedStandardPayload,
} from './normatives';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class NormativesService {
  private readonly apiBaseUrl = '/schedules/standards';
  private readonly sectorSpeedStandardsPageLimit = 30;
  // Предохранитель от бесконечной догрузки, если бэкенд проигнорирует offset
  private readonly sectorSpeedStandardsMaxPages = 200;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  public getMarketBranches(): Observable<CustomHttpResponse<IMarketBranchResponse[]>> {
    return this.http.get<CustomHttpResponse<IMarketBranchResponse[]>>(`${this.apiBaseUrl}/get_market_branches/`).pipe(catchError(this.handleError('Ошибка при загрузке филиалов')));
  }

  public getRegionStandards(marketBranchId: number, limit?: number, offset?: number): Observable<IStandardsResponse<IRegionStandard>> {
    let params = new HttpParams().set('market_branch_id', marketBranchId.toString());

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    return this.http.get<CustomHttpResponse<IRegionStandardsResponse>>(`${this.apiBaseUrl}/get_region_standards/`, { params }).pipe(
      map((response) => ({ standards: response.data.region_standards })),
      catchError(this.handleError(`Ошибка при загрузке нормативов региона для филиала ${marketBranchId}`))
    );
  }

  public getSectorStandards(marketBranchId: number, sectorId: number, limit?: number, offset?: number): Observable<IStandardsResponse<ISectorStandard>> {
    let params = new HttpParams().set('market_branch_id', marketBranchId.toString()).set('sector_id', sectorId.toString());

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    return this.http.get<CustomHttpResponse<ISectorStandardsResponse>>(`${this.apiBaseUrl}/get_sector_standards/`, { params }).pipe(
      map((response) => ({ standards: response.data.sector_standards })),
      catchError(this.handleError(`Ошибка при загрузке нормативов участка ${sectorId} для филиала ${marketBranchId}`))
    );
  }

  public getCountryStandards(limit?: number, offset?: number): Observable<IStandardsResponse<ICountryStandard>> {
    let params = new HttpParams();

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    const httpOptions = params.keys().length > 0 ? { params } : {};

    console.log('getCountryStandards called with:', { limit, offset, httpOptions });

    return this.http.get<any>(`${this.apiBaseUrl}/get_country_standards/`, httpOptions).pipe(
      map((response) => {
        console.log('getCountryStandards raw response:', response);
        // Проверяем, есть ли вложенная структура data.data
        const standards = (response.data && response.data.data && response.data.data.country_standards) || (response.data && response.data.country_standards) || [];
        console.log('getCountryStandards extracted standards:', standards.length);
        return { standards };
      }),
      catchError(this.handleError('Ошибка при загрузке нормативов РФ'))
    );
  }

  public getFederalStandards(regionName: string, limit?: number, offset?: number): Observable<IStandardsResponse<IFederalStandard>> {
    let params = new HttpParams().set('region_name', regionName);

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    return this.http.get<CustomHttpResponse<IFederalStandardsResponse>>(`${this.apiBaseUrl}/get_federal_standards/`, { params }).pipe(
      map((response) => ({ standards: response.data.federal_standards })),
      catchError(this.handleError(`Ошибка при загрузке нормативов для региона ${regionName}`))
    );
  }

  public updateCountryStandard(entity_id: number, value: number, cascade: boolean): Observable<IUpdateResponse> {
    const payload: IUpdateStandardPayload = {
      entity_id,
      value,
      user_id: this.authService.getAuthKey('u_id'),
      cascade,
    };
    return this.http.post<IUpdateResponse>(`${this.apiBaseUrl}/update_country_standards/`, payload).pipe(catchError(this.handleError('Ошибка при обновлении норматива страны')));
  }

  public updateFederalStandard(entity_id: number, value: number, cascade: boolean): Observable<IUpdateResponse> {
    const payload: IUpdateStandardPayload = {
      entity_id,
      value,
      user_id: this.authService.getAuthKey('u_id'),
      cascade,
    };
    return this.http.post<IUpdateResponse>(`${this.apiBaseUrl}/update_federal_standards/`, payload).pipe(catchError(this.handleError('Ошибка при обновлении норматива округа')));
  }

  public updateRegionStandard(entity_id: number, value: number, cascade: boolean): Observable<IUpdateResponse> {
    const payload: IUpdateStandardPayload = {
      entity_id,
      value,
      user_id: this.authService.getAuthKey('u_id'),
      cascade,
    };
    return this.http.post<IUpdateResponse>(`${this.apiBaseUrl}/update_region_standards/`, payload).pipe(catchError(this.handleError('Ошибка при обновлении норматива региона')));
  }

  public updateSectorStandard(entity_id: number, value: number, cascade: boolean): Observable<IUpdateResponse> {
    const payload: IUpdateStandardPayload = {
      entity_id,
      value,
      user_id: this.authService.getAuthKey('u_id'),
      cascade,
    };
    return this.http.post<IUpdateResponse>(`${this.apiBaseUrl}/update_sector_standards/`, payload).pipe(catchError(this.handleError('Ошибка при обновлении норматива участка')));
  }

  /**
   * Нормативы скорости участков (одна страница).
   *
   * Бэкенд ESAS проксирует внутренний GET /internal/standards/v1/sector_speed_standards
   * и сам подставляет заголовок x-api-key.
   */
  public getSectorSpeedStandards(params?: ISectorSpeedStandardsParams): Observable<ISectorSpeedStandard[]> {
    return this.http.get<any>(`${this.apiBaseUrl}/get_sector_speed_standards/`, { params: this.buildSectorSpeedStandardsParams(params) }).pipe(
      map((response) => this.extractSectorSpeedStandards(response)),
      catchError(this.handleError('Ошибка при загрузке нормативов скорости'))
    );
  }

  /**
   * Нормативы скорости участков целиком: догружает страницы, пока бэкенд отдаёт полную страницу
   */
  public getAllSectorSpeedStandards(params?: ISectorSpeedStandardsParams): Observable<ISectorSpeedStandard[]> {
    const limit = this.sectorSpeedStandardsPageLimit;
    const maxPages = this.sectorSpeedStandardsMaxPages;
    let offset = 0;
    let page = 1;

    return this.getSectorSpeedStandards(Object.assign({}, params, { limit, offset })).pipe(
      expand((standards: ISectorSpeedStandard[]) => {
        if (standards.length < limit || page >= maxPages) {
          return EMPTY;
        }

        offset += standards.length;
        page += 1;

        return this.getSectorSpeedStandards(Object.assign({}, params, { limit, offset }));
      }),
      reduce((acc: ISectorSpeedStandard[], standards: ISectorSpeedStandard[]) => acc.concat(standards), [] as ISectorSpeedStandard[])
    );
  }

  /**
   * Обновить норматив скорости участка.
   *
   * POST /schedules/standards/update_sector_speed_standards/ с телом { entity_id, value }
   * (cascade и user_id этот эндпоинт не принимает, в отличие от нормативов работ)
   */
  public updateSectorSpeedStandard(entity_id: number, value: number): Observable<IUpdateResponse> {
    const payload: IUpdateSectorSpeedStandardPayload = { entity_id, value };

    return this.http
      .post<IUpdateResponse>(`${this.apiBaseUrl}/update_sector_speed_standards/`, payload)
      .pipe(catchError(this.handleError('Ошибка при обновлении норматива скорости')));
  }

  private buildSectorSpeedStandardsParams(params?: ISectorSpeedStandardsParams): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      httpParams = httpParams.set('offset', params.offset.toString());
    }

    httpParams = this.appendArrayParam(httpParams, 'id', params.id);
    httpParams = this.appendArrayParam(httpParams, 'sector_id', params.sector_id);
    httpParams = this.appendArrayParam(httpParams, 'vehicle_type_id', params.vehicle_type_id);
    httpParams = this.appendArrayParam(httpParams, 'value', params.value);

    return httpParams;
  }

  /**
   * id, sector_id, vehicle_type_id и value описаны в Swagger как array<integer>,
   * поэтому каждое значение уходит отдельным одноимённым query-параметром
   */
  private appendArrayParam(httpParams: HttpParams, key: string, values?: number[]): HttpParams {
    if (!values || !values.length) {
      return httpParams;
    }

    return values.reduce((acc, value) => acc.append(key, value.toString()), httpParams);
  }

  /**
   * Внутренний сервис отдаёт плоский список, бэкенд ESAS оборачивает его в { data, metadata }.
   * Допускаем оба варианта и вложенный ключ — как в get_country_standards
   */
  private extractSectorSpeedStandards(response: any): ISectorSpeedStandard[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data && Array.isArray(response.data.sector_speed_standards)) {
      return response.data.sector_speed_standards;
    }
    if (response.data && response.data.data && Array.isArray(response.data.data.sector_speed_standards)) {
      return response.data.data.sector_speed_standards;
    }

    return [];
  }

  /**
   * Преобразует стандарты в сводную таблицу для отображения
   *
   * Пример: Если есть два норматива с названием "Дорога до УК, мин." но разными ID:
   * - "Дорога до УК, мин. (ID: 1)"
   * - "Дорога до УК, мин. (ID: 2)"
   *
   * @param standards Массив стандартов
   * @returns Объект с заголовками и строками таблицы
   */
  public transformStandardsToPivotTable(standards: IBaseStandard[]): { headers: string[]; rows: INormativeRow[] } {
    if (!standards || !standards.length) {
      return { headers: [], rows: [] };
    }

    // Разделяем стандарты на обычные и итоговые
    const regularStandards = standards.filter((s) => s.standard.description !== 'База норматив, мин.');
    const totalStandards = standards.filter((s) => s.standard.description === 'База норматив, мин.');

    const standardKeys = regularStandards
      .map((s) => ({
        id: s.standard_id,
        description: s.standard.description,
        key: `${s.standard_id}_${s.standard.description}`,
      }))
      .filter((value, index, self) => self.findIndex((item) => item.key === value.key) === index);

    // Формируем заголовки
    const headerLabels = standardKeys.map((standardKey) => standardKey.description);
    // Итоговый столбец теперь называется 'База норматив, мин.'
    const headers = ['Название работы'].concat(headerLabels, ['База норматив, мин.']);

    const groupedByWorkType = standards.reduce(
      (acc, item) => {
        const workType = item.work_type;
        if (!acc[workType.id]) {
          acc[workType.id] = {
            name: workType.description,
            items: {},
          };
        }
        const standardKey = `${item.standard_id}_${item.standard.description}`;
        acc[workType.id].items[standardKey] = item;
        return acc;
      },
      {} as Record<number, { name: string; items: Record<string, IBaseStandard> }>
    );

    const rows: INormativeRow[] = Object.values(groupedByWorkType).map((group) => {
      let total = 0;
      const cells = standardKeys.map((standardKey) => {
        const standardItem = group.items[standardKey.key] as IBaseStandard;
        if (standardItem) {
          total += standardItem.value || 0;
          return {
            id: standardItem.id,
            standardId: standardItem.standard_id,
            value: standardItem.value,
            isEditing: false,
            originalValue: standardItem.value,
            isChanged: false,
          };
        }
        return null;
      });

      // Ищем итоговую ячейку для этой группы работ
      // Получаем work_type.id из первого элемента группы
      const firstItemKey = Object.keys(group.items)[0];
      const workTypeId = firstItemKey ? Object.values(group.items)[0].work_type.id : null;
      const totalStandardForGroup = totalStandards.find((ts) => ts.work_type.id === workTypeId);
      let totalCell: INormativeCell | null = null;

      if (totalStandardForGroup) {
        totalCell = {
          id: totalStandardForGroup.id,
          standardId: totalStandardForGroup.standard_id,
          value: totalStandardForGroup.value,
          isEditing: false,
          originalValue: totalStandardForGroup.value,
          isChanged: false,
        };
      }

      // Добавляем итоговую ячейку в конец массива cells
      const allCells = cells.concat([totalCell]);

      return {
        workTypeName: group.name,
        cells: allCells,
        total: totalCell ? totalCell.value : total,
      };
    });

    return { headers, rows };
  }

  /**
   * Преобразование стандартов в нормативы для отображения
   * @param response Ответ с сервера
   */
  public transformStandardsToNormatives(standards: IRegionStandard[]): INormativeView[] {
    if (!standards || !standards.length) {
      console.log('Ответ пуст или не содержит данных о нормативах');
      return [];
    }

    console.log(`Преобразование ${standards.length} записей нормативов`);

    // Преобразование данных от API в формат для отображения в таблице
    return standards.map((standard) => {
      // Расчет компонентов времени (это пример, можно адаптировать под реальные данные)
      const total = standard.value;
      const driveToCDC = Math.round(total * 0.2);
      const driveToClient = Math.round(total * 0.1);
      const driveToCDCBack = Math.round(total * 0.1);
      const documents = Math.round(total * 0.1);
      const technical = total - driveToCDC - driveToClient - driveToCDCBack - documents;

      return {
        id: standard.id,
        name: standard.work_type.description,
        driveToCDC,
        driveToClient,
        technical,
        documents,
        driveToCDCBack,
        total,
        regionId: standard.market_branch.mb_region_id,
        branchId: standard.market_branch_id,
        sectorId: standard.market_branch.next_sector_id,
      };
    });
  }

  private handleError(operation = 'operation') {
    return (error: HttpErrorResponse): Observable<never> => {
      console.error(`${operation} завершилась с ошибкой:`, error);
      return throwError(() => error);
    };
  }
}
